import { QueryClient, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import {
  VIDEO_THEME_CLIP_API_URL,
  CLIP_BY_VIDEO_THEME_ID_QUERY_KEY,
  ALL_VIDEO_THEME_CLIP_QUERY_KEY,
  VIDEO_THEME_CLIP_BY_ID_QUERY_KEY,
  GET_VIDEO_THEME_CLIPS
} from '@/constants';
import { apiUrl } from '@/helpers';
import { ErrorResponse, Options } from '@/types';
import { CreateEditVideoThemeClip } from '@/types/VideoThemeTypes';

export const videoThemeClipsEndpoints = {
  getClipsByVideoThemeId: (videoThemeId: number) =>
    axios.get(`${apiUrl()}${VIDEO_THEME_CLIP_API_URL}/videotheme/${videoThemeId}`),
  deleteVideoThemeClip: (id: number) =>
    axios.delete(`${apiUrl()}${VIDEO_THEME_CLIP_API_URL}/${id}`),
  getVideoThemeClipById: (id: number) => axios.get(`${apiUrl()}${VIDEO_THEME_CLIP_API_URL}/${id}`),
  createVideoThemeClip: (payload: CreateEditVideoThemeClip) =>
    axios.post(`${apiUrl()}${VIDEO_THEME_CLIP_API_URL}`, payload),
  updateVideoThemeClip: (payload: CreateEditVideoThemeClip) =>
    axios.put(`${apiUrl()}${VIDEO_THEME_CLIP_API_URL}`, payload)
};

export const useGetClipsByVideoThemeIdQuery = (
  videoThemeId: number,
  options?: Options,
  enabled?: boolean
) => {
  return useQuery(
    [CLIP_BY_VIDEO_THEME_ID_QUERY_KEY, { videoThemeId }],
    () => videoThemeClipsEndpoints.getClipsByVideoThemeId(videoThemeId),
    {
      enabled: !!videoThemeId && (enabled ?? true),
      onError: (error) => {
        options?.onError(error as ErrorResponse);
      }
    }
  );
};

export const useDeleteVideoThemeClipMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(videoThemeClipsEndpoints.deleteVideoThemeClip, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_VIDEO_THEME_CLIP_QUERY_KEY]);
    }
  });
};

export const useGetVideoThemeClipByIdQuery = (id: number, options?: Options) => {
  return useQuery(
    [VIDEO_THEME_CLIP_BY_ID_QUERY_KEY, { id }],
    () => videoThemeClipsEndpoints.getVideoThemeClipById(id),
    {
      enabled: !!id,
      onError: (error) => {
        options?.onError(error as ErrorResponse);
      }
    }
  );
};

export const useCreateVideoThemeClipMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(videoThemeClipsEndpoints.createVideoThemeClip, {
    onSuccess: (response) => {
      options.onSuccess(response);
      queryClient.invalidateQueries([GET_VIDEO_THEME_CLIPS]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdateVideoThemeClip = (queryClient: QueryClient, options: Options) => {
  return useMutation(videoThemeClipsEndpoints.updateVideoThemeClip, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([VIDEO_THEME_CLIP_BY_ID_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};
